import React, { useCallback, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { useTheme } from "@mui/material/styles";
import isEqual from "lodash/isEqual";
import { Button, Dialog, DialogContent, Grid, IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as TravelIcons from "../../assets/travel-expense-icons";
import * as LocationIcons from "../../assets/location-types";
import { CustomIconButton } from "./styles";
import SvgFromString from "./SvgFromString/SvgFromString";
import DialogActions from "../../containers/DialogActions/DialogActions";
import DialogTitle from "../../containers/DialogTitle/DialogTitle";

interface Props {
  handleNotifyParent: (svg: string) => void;
  selectedIcon?: string;
  isLocationIcons?: boolean;
}

interface State {
  isDialogOpen: boolean;
  loading: boolean;
  svgContent: React.FunctionComponent<
    React.SVGProps<SVGSVGElement> & {
      title?: string | undefined;
    }
  > | null;
}

const IconsSelect = ({ handleNotifyParent, selectedIcon, isLocationIcons = false }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [state, setState] = useState<State>({
    isDialogOpen: false,
    loading: false,
    svgContent: null,
  });

  const renderSvgToString = (
    SvgComponent: React.FunctionComponent<
      React.SVGProps<SVGSVGElement> & {
        title?: string | undefined;
      }
    >
  ) => {
    const svgString = ReactDOMServer.renderToStaticMarkup(<SvgComponent />);
    return svgString;
  };

  const handleClose = useCallback(() => {
    setState((state) => ({
      ...state,
      isDialogOpen: false,
      loading: false,
      svgContent: null,
    }));
  }, []);

  const handleConfirm = useCallback(() => {
    if (!state.svgContent) return;

    const svgToString = renderSvgToString(state.svgContent);

    handleNotifyParent(svgToString);

    handleClose();
  }, [handleNotifyParent, state.svgContent]);

  const IconComponents = isLocationIcons ? Object.values(LocationIcons) : Object.values(TravelIcons);

  return (
    <>
      <Grid
        onClick={() => {
          setState((state) => ({ ...state, isDialogOpen: true }));
        }}
      >
        {!!selectedIcon ? (
          <IconButton
            style={{
              color: theme.palette.primary.main,
              border: `1px solid ${theme.palette.secondary.light}`,
              borderRadius: "5px",
              boxShadow: `2px 2px 5px ${theme.palette.secondary.light}`,
            }}
          >
            <SvgFromString
              svgString={selectedIcon}
              color={theme.palette.secondary.light}
              width={"30px"}
            />
          </IconButton>
        ) : (
          <Button
            style={{ textTransform: "none" }}
            variant="contained"
          >
            Select icon
          </Button>
        )}
      </Grid>

      <Dialog
        open={state.isDialogOpen}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          title={isLocationIcons ? "Select location type icon" : "Select travel expense type icon"}
          handleClose={handleClose}
        />
        <DialogContent>
          <Grid sx={{ padding: "10px 0px" }}>
            <div
              style={{
                width: "100%",
                height: "50vh",
                overflow: "auto",
                display: "flex",
                gap: "20px",
                flexWrap: "wrap",
                // justifyContent : "start",
                alignContent: "start",
              }}
            >
              {!!IconComponents.length &&
                IconComponents.map((IconComponent, index) => {
                  const selectedIcon = isEqual(IconComponent, state.svgContent);

                  return (
                    <CustomIconButton
                      key={`icon-${index}`}
                      onClick={() =>
                        setState((state) => ({
                          ...state,
                          svgContent: IconComponent,
                        }))
                      }
                      isActive={selectedIcon}
                    >
                      <IconComponent
                        width={40}
                        height={40}
                      />
                    </CustomIconButton>
                  );
                })}
            </div>
          </Grid>
        </DialogContent>
        <DialogActions
          handleSave={handleConfirm}
          handleCancel={handleClose}
          customSaveName={`${t(`buttons.confirm`)}`}
        />
      </Dialog>
    </>
  );
};

export default IconsSelect;
